export type BidderCountSource = 'unique' | 'number_of_bids'

export type BidderCount = {
  bidderCount: number | null
  bidderCountSource: BidderCountSource | null
}

export type BidderCountSegmentProps = {
  bidder_count: number | null
  bidder_count_source: BidderCountSource | null
}

/** The SearchResult-compatible shape every emitter already holds. */
export type BidderCountInput = {
  numberOfBids?: number | null
  auction3MP?: { uniqueBidders?: number | null } | null
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

/**
 * GLOB-5164 — a listing's bidder count is a listing fact, selected here so the
 * low-bidder gate and every analytics emitter agree on it.
 *
 * Prefers the unique-bidder count over the raw bid count. An unknown count is
 * `null`, never `0` — a coalesced zero would pass every bidder gate — and the
 * source is `null` exactly when the count is.
 */
export const getBidderCount = ({
  numberOfBids,
  auction3MP,
}: BidderCountInput): BidderCount => {
  const uniqueBidders = auction3MP?.uniqueBidders
  if (isFiniteNumber(uniqueBidders)) {
    return { bidderCount: uniqueBidders, bidderCountSource: 'unique' }
  }
  if (isFiniteNumber(numberOfBids)) {
    return { bidderCount: numberOfBids, bidderCountSource: 'number_of_bids' }
  }
  return { bidderCount: null, bidderCountSource: null }
}

/**
 * The only place the `bidder_count` / `bidder_count_source` Segment property
 * names should be spelled.
 */
export const toBidderCountSegmentProps = (
  listing: BidderCountInput
): BidderCountSegmentProps => {
  const { bidderCount, bidderCountSource } = getBidderCount(listing)
  return { bidder_count: bidderCount, bidder_count_source: bidderCountSource }
}
